/**
 * Google Analytics 4 (gtag.js) initialization.
 *
 * The project enforces a strict CSP that forbids inline scripts, so the
 * gtag.js library is loaded programmatically here (CSP-allowlisted domain),
 * and the config command runs in module scope instead of an inline snippet.
 *
 * Measurement IDs are publishable identifiers (not secrets), so it is safe
 * to reference this constant in client code.
 */
import { logger } from "@/lib/logger";

export const GA_MEASUREMENT_ID = "G-Z06H2EVLSP";

let initialized = false;

/** Load gtag.js and send the initial config pageview. Idempotent. */
export function initGoogleAnalytics(): void {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  window.dataLayer = window.dataLayer || [];

  function gtag(...args: unknown[]): void {
    window.dataLayer!.push(args);
  }
  window.gtag = gtag;

  gtag("js", new Date());
  gtag("config", GA_MEASUREMENT_ID);

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  script.onerror = () => logger.error("[analytics] Failed to load gtag.js");
  document.head.appendChild(script);

  logger.info(`[analytics] Google Analytics initialized (${GA_MEASUREMENT_ID})`);
}
